import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Music, TrendingUp, Users, BarChart3, ArrowLeft, RefreshCw } from 'lucide-react';
import '../styles/bmgStreaming.css';

const BMGStreaming = () => {
  const [view, setView] = useState('grid'); // 'grid' or 'detail'
  const [artists, setArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [artistInsights, setArtistInsights] = useState(null);
  const [billboardData, setBillboardData] = useState([]);
  const [appleChartData, setAppleChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [chartModal, setChartModal] = useState({ isOpen: false, chartType: null, chartData: null });

  const API_BASE = 'https://n8n.iniai.cloud/webhook';

  // Fetch all artists on mount
  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching from:', `${API_BASE}/current-snapshot`);
      console.log('API Key exists?', !!process.env.REACT_APP_CREATE_USER_API_KEY);

      const response = await fetch(`${API_BASE}/current-snapshot`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response ok?', response.ok);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Failed to fetch artists: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Debug: log the response structure
      console.log('API Response:', data);
      console.log('API Response type:', typeof data);
      console.log('Is Array?', Array.isArray(data));

      // Handle if data is wrapped in an object
      const artistsData = Array.isArray(data) ? data : (data.artists || data.data || []);
      console.log('Artists Data:', artistsData);
      console.log('Artists Data length:', artistsData.length);

      setArtists(artistsData);
    } catch (err) {
      console.error('Error fetching artists:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtistDetails = async (artistName, artistId) => {
    try {
      setDetailLoading(true);
      setError(null);
      const encodedName = encodeURIComponent(artistName);

      const fetchOptions = {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      };

      // Fetch all data sources in parallel - failures are independent
      const [timeseriesResult, insightsResult, billboardResult, appleChartResult] = await Promise.allSettled([
        // Time series data
        fetch(`${API_BASE}/daily-timeseries?artist_name=${encodedName}`, fetchOptions)
          .then(res => res.ok ? res.json() : Promise.reject('Timeseries failed')),

        // AI insights
        fetch(`${API_BASE}/artist-insights?artist_name=${encodedName}`, fetchOptions)
          .then(res => {
            console.log('Insights response status:', res.status);
            return res.ok ? res.json() : Promise.reject('Insights failed');
          }),

        // Billboard trajectory with cache busting
        fetch(`${API_BASE}/billboard-trajectory?artist_name=${encodedName}&_t=${Date.now()}`, fetchOptions)
          .then(res => {
            console.log('Billboard response status:', res.status);
            return res.ok ? res.json() : Promise.reject('Billboard failed');
          }),

        // Apple Music chart data by country/genre
        artistId
          ? fetch(`${API_BASE}/apple-chart-data?artist_id=${artistId}`, fetchOptions)
              .then(res => res.ok ? res.json() : Promise.reject('Apple chart data failed'))
          : Promise.reject('No artist_id')
      ]);

      // Handle timeseries data
      if (timeseriesResult.status === 'fulfilled') {
        const timeseriesArray = Array.isArray(timeseriesResult.value) ? timeseriesResult.value : [];
        console.log('Timeseries loaded:', timeseriesArray.length, 'records');
        setTimeSeriesData(timeseriesArray);
      } else {
        console.error('Timeseries error:', timeseriesResult.reason);
        setTimeSeriesData([]);
      }

      // Handle insights data
      if (insightsResult.status === 'fulfilled') {
        const insights = Array.isArray(insightsResult.value) ? insightsResult.value[0] : insightsResult.value;
        console.log('Insights loaded:', insights);
        setArtistInsights(insights);
      } else {
        console.error('Insights error:', insightsResult.reason);
        setArtistInsights(null);
      }

      // Handle Billboard data
      if (billboardResult.status === 'fulfilled') {
        const billboardArray = Array.isArray(billboardResult.value) ? billboardResult.value : [];
        console.log('Billboard loaded:', billboardArray.length, 'records');
        console.log('Billboard data:', billboardArray);
        setBillboardData(billboardArray);
      } else {
        console.error('Billboard error:', billboardResult.reason);
        setBillboardData([]);
      }

      // Handle Apple Chart data
      if (appleChartResult.status === 'fulfilled') {
        const appleArray = Array.isArray(appleChartResult.value) ? appleChartResult.value : [];
        console.log('Apple chart data loaded:', appleArray.length, 'records');
        setAppleChartData(groupAppleChartData(appleArray));
      } else {
        console.error('Apple chart error:', appleChartResult.reason);
        setAppleChartData(null);
      }

    } catch (err) {
      console.error('Critical error in fetchArtistDetails:', err);
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleArtistClick = (artist) => {
    setSelectedArtist(artist);
    setView('detail');
    fetchArtistDetails(artist.artist_name, artist.artist_id);
  };

  const handleBackClick = () => {
    // Clear state first, then change view to avoid rendering issues
    setSelectedArtist(null);
    setTimeSeriesData([]);
    setArtistInsights(null);
    setBillboardData([]);
    setAppleChartData(null);
    // Use setTimeout to ensure state is cleared before view changes
    setTimeout(() => setView('grid'), 0);
  };

  const handleRefreshInsights = async () => {
    if (!selectedArtist) return;

    try {
      setRefreshing(true);
      const response = await fetch(`${API_BASE}/generate-insights`, {
        method: 'POST',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          artist_id: selectedArtist.artist_id,
          force_refresh: true
        })
      });

      if (!response.ok) throw new Error('Failed to refresh insights');

      // Refetch insights after generation (wait for completion)
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchArtistDetails(selectedArtist.artist_name, selectedArtist.artist_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const formatFollowers = (num) => {
    if (!num || isNaN(num)) return 'N/A';
    try {
      const numValue = Number(num);
      if (numValue >= 1000000) return `${(numValue / 1000000).toFixed(1)}M`;
      if (numValue >= 1000) return `${(numValue / 1000).toFixed(1)}K`;
      return numValue.toString();
    } catch (e) {
      return 'N/A';
    }
  };

  // Adaptive formatter for Y-axis - shows more precision when range is tight
  const formatFollowersAdaptive = (num, dataArray) => {
    if (!num || isNaN(num)) return 'N/A';
    try {
      const numValue = Number(num);

      // Calculate appropriate decimal places based on data range
      let decimalPlaces = 1; // default
      if (dataArray && dataArray.length > 0) {
        const followers = dataArray.map(d => d.spotify_followers).filter(f => f != null);
        if (followers.length > 0) {
          const min = Math.min(...followers);
          const max = Math.max(...followers);
          const range = max - min;

          // If range is very small relative to scale, show more precision
          if (range < max * 0.01) {
            // Less than 1% variation - use 2 decimal places
            decimalPlaces = 2;
          }
        }
      }

      if (numValue >= 1000000) {
        return `${(numValue / 1000000).toFixed(decimalPlaces)}M`;
      }
      if (numValue >= 1000) {
        return `${(numValue / 1000).toFixed(decimalPlaces)}K`;
      }
      return numValue.toString();
    } catch (e) {
      return 'N/A';
    }
  };

  // Exact formatter for tooltips - shows full number with commas
  const formatFollowersExact = (num) => {
    if (!num || isNaN(num)) return 'N/A';
    try {
      return Number(num).toLocaleString('en-US');
    } catch (e) {
      return 'N/A';
    }
  };

  const getFollowerDomain = (data) => {
    if (!data || data.length === 0) return ['auto', 'auto'];
    const followers = data.map(d => d.spotify_followers).filter(f => f != null);
    const min = Math.min(...followers);
    const max = Math.max(...followers);
    const range = max - min;

    // If range is very small (flat line), add padding to show some variation
    if (range < max * 0.01) {
      // Less than 1% variation - zoom in to show detail
      return [
        Math.floor(min - max * 0.005),
        Math.ceil(max + max * 0.005)
      ];
    }

    // Add 10% padding on each side
    return [
      Math.floor(min - range * 0.1),
      Math.ceil(max + range * 0.1)
    ];
  };

  const getPopularityDomain = (data) => {
    if (!data || data.length === 0) return [0, 100];
    const popularity = data.map(d => d.spotify_popularity).filter(p => p != null);
    const min = Math.min(...popularity);
    const max = Math.max(...popularity);
    const range = max - min;

    // If range is very small (flat line), use a narrower window
    if (range < 5) {
      // Less than 5 points variation - use a 10-point window
      return [
        Math.max(0, Math.floor(min - 5)),
        Math.min(100, Math.ceil(max + 5))
      ];
    }

    // Add some padding but stay within 0-100
    return [
      Math.max(0, Math.floor(min - range * 0.2)),
      Math.min(100, Math.ceil(max + range * 0.2))
    ];
  };

  const getTierClass = (tier) => {
    const tierMap = {
      'Mega-Star': 'bmg-tier-mega-star',
      'Established': 'bmg-tier-established',
      'Mid-Tier': 'bmg-tier-mid-tier',
      'Emerging': 'bmg-tier-emerging',
      'Legacy': 'bmg-tier-legacy'
    };
    return tierMap[tier] || 'bmg-tier-emerging';
  };

  const getMomentumClass = (momentum) => {
    const momentumMap = {
      'Rising': 'bmg-momentum-rising',
      'Stable': 'bmg-momentum-stable',
      'Declining': 'bmg-momentum-declining'
    };
    return momentumMap[momentum] || 'bmg-momentum-stable';
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  };

  const formatReleaseDate = (dateString) => {
    try {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return null;
    }
  };

  const formatReleaseDateShort = (dateString) => {
    try {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch (e) {
      return null;
    }
  };

  const COUNTRY_CONFIG = [
    { code: 'us', flag: '\u{1F1FA}\u{1F1F8}', name: 'United States' },
    { code: 'gb', flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom' },
    { code: 'de', flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany' },
    { code: 'fr', flag: '\u{1F1EB}\u{1F1F7}', name: 'France' },
    { code: 'jp', flag: '\u{1F1EF}\u{1F1F5}', name: 'Japan' },
    { code: 'br', flag: '\u{1F1E7}\u{1F1F7}', name: 'Brazil' },
    { code: 'au', flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia' },
  ];

  const groupAppleChartData = (rows) => {
    if (!rows || rows.length === 0) return null;
    const byCountry = {};
    rows.forEach(row => {
      const cc = row.country_code;
      if (!byCountry[cc]) byCountry[cc] = { overall: null, genres: [] };
      if (row.chart_type === 'overall') {
        byCountry[cc].overall = row;
      } else {
        byCountry[cc].genres.push(row);
      }
    });
    return byCountry;
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const generated = new Date(timestamp);
    const hours = Math.floor((now - generated) / (1000 * 60 * 60));
    if (hours < 1) return 'Less than an hour ago';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const formatKeyOpportunities = (text) => {
    if (!text) return text;

    // Check if text has markdown formatting (**) or is plain text
    const hasMarkdown = text.includes('**');

    let items;
    if (hasMarkdown) {
      // Check if it's numbered format "**1. Title**" (period) or "**1) Title:**" (parenthesis)
      if (text.match(/\*\*\d+\.\s+/)) {
        // Split by **1., **2., etc.
        items = text.split(/(?=\*\*\d+\.)/);
      } else if (text.match(/\*\*\d+\)/)) {
        // Split by **1), **2), etc.
        items = text.split(/(?=\*\*\d+\))/);
      } else if (text.match(/\*\*[^*]+:/)) {
        // Unnumbered format with colon: "**Title:** content" (colon after asterisks)
        items = text.split(/(?=\*\*[^*]+:)/);
      } else if (text.match(/\*\*[^*]+\*\*\s*—/)) {
        // Unnumbered format with em dash: "**Title** — content"
        items = text.split(/(?=\*\*[^*]+\*\*\s*—)/);
      } else {
        // Default: split by ** at line starts
        items = [text];
      }
    } else {
      // Check if plain text uses periods or parentheses
      if (text.match(/\d+\.\s+[A-Z]/)) {
        // Split by 1., 2., etc.
        items = text.split(/(?=\d+\.\s+[A-Z])/);
      } else {
        // Split by 1), 2), etc.
        items = text.split(/(?=\d+\)\s+[A-Z])/);
      }
    }

    return items.map((item, index) => {
      const trimmed = item.trim();
      if (!trimmed) return null;

      if (hasMarkdown) {
        // Check patterns with CLOSING asterisks first (more specific)

        // Try format: "**1. Title** - content" (closing asterisks with dash separator)
        let match = trimmed.match(/^\*\*(\d+\.)\s+([^*]+)\*\*\s*-\s*(.*)/s);
        if (match) {
          const [, number, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{number} {title.trim()}:</strong> {content.trim()}
            </div>
          );
        }

        // Try format: "**1. Title**: content" (closing asterisks with colon after)
        match = trimmed.match(/^\*\*(\d+\.)\s+([^*]+)\*\*:\s*(.*)/s);
        if (match) {
          const [, number, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{number} {title.trim()}:</strong> {content.trim()}
            </div>
          );
        }

        // Try format: "**1. Title:** content" (opening asterisks only, no closing)
        match = trimmed.match(/^\*\*(\d+\.)\s+([^:]+):\s*(.*)/s);
        if (match) {
          const [, number, title, content] = match;
          // Remove any leftover ** at the start of content
          const cleanContent = content.trim().replace(/^\*\*\s*/, '');
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{number} {title.trim()}:</strong> {cleanContent}
            </div>
          );
        }

        // Try format: "**1) Title:** content"
        match = trimmed.match(/^\*\*(\d+\))\s+([^*]+):\*\*\s*(.*)/s);
        if (match) {
          const [, number, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{number} {title.trim()}:</strong> {content.trim()}
            </div>
          );
        }

        // Try unnumbered format: "**Title** — content" (em dash, no numbers)
        match = trimmed.match(/^\*\*([^*]+)\*\*\s*—\s*(.*)/s);
        if (match) {
          const [, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{title.trim()}:</strong> {content.trim()}
            </div>
          );
        }

        // Try unnumbered format: "**Title:** content" (colon after closing asterisks)
        match = trimmed.match(/^\*\*([^*]+)\*\*:\s*(.*)/s);
        if (match) {
          const [, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{title.trim()}:</strong> {content.trim()}
            </div>
          );
        }

        // Try format: "**Title:** content" (no closing asterisks before colon - title may have colons)
        match = trimmed.match(/^\*\*([^*]+):\s*(.*)/s);
        if (match) {
          const [, title, content] = match;
          // Check if content starts with ** (means closing asterisks are after colon)
          const cleanContent = content.trim().replace(/^\*\*\s*/, '');
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{title.trim()}:</strong> {cleanContent}
            </div>
          );
        }
      } else {
        // Try plain text with period: "1. Title: content"
        let match = trimmed.match(/^(\d+\.)\s+([^:]+):\s*(.*)/s);
        if (match) {
          const [, number, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{number} {title.trim()}:</strong> {content.trim()}
            </div>
          );
        }

        // Try plain text with parenthesis: "1) Title: content" or "1) Title—content"
        match = trimmed.match(/^(\d+\))\s+([^:—]+)[:\—]\s*(.*)/s);
        if (match) {
          const [, number, title, content] = match;
          return (
            <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', marginBottom: '8px', fontSize: '14px' }}>
              <strong>{number} {title.trim()}:</strong> {content.trim()}
            </div>
          );
        }
      }

      // Fallback if pattern doesn't match
      return <div key={index} style={{ marginTop: index > 0 ? '16px' : '0', fontSize: '14px' }}>{trimmed}</div>;
    }).filter(Boolean);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="bmg-container">
        <div className="bmg-loading">Loading artist data...</div>
      </div>
    );
  }

  // Render error state
  if (error && view === 'grid') {
    return (
      <div className="bmg-container">
        <div className="bmg-error">
          <p>Error: {error}</p>
          <button onClick={fetchArtists} className="bmg-refresh-button">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // Render grid view
  if (view === 'grid') {
    return (
      <div className="bmg-container">
        <div className="bmg-header">
          <h1>Streaming Intelligence</h1>
          <p>{artists.length} artists tracked across Spotify, Apple Music, and Billboard Hot 100</p>
        </div>

        <div className="bmg-artist-grid">
          {artists.map((artist) => (
            <div
              key={artist.artist_name}
              className="bmg-artist-card"
              onClick={() => handleArtistClick(artist)}
            >
              <div className="bmg-artist-card-header">
                <h3 className="bmg-artist-name">{artist.artist_name}</h3>
                <span className={`bmg-tier-badge ${getTierClass(artist.artist_tier)}`}>
                  {artist.artist_tier}
                </span>
              </div>

              <div className="bmg-popularity-section">
                <div className="bmg-popularity-label">Spotify Popularity</div>
                <div className="bmg-popularity-bar">
                  <div
                    className="bmg-popularity-fill"
                    style={{ width: `${artist.spotify_popularity}%` }}
                  />
                </div>
                <div className="bmg-popularity-value">{artist.spotify_popularity}</div>
              </div>

              <div className="bmg-followers">
                <strong>{formatFollowers(artist.spotify_followers)}</strong> followers
                {artist.monthly_listeners && (
                  <span className="bmg-monthly-listeners">
                    • <strong>{formatFollowers(artist.monthly_listeners)}</strong> monthly
                  </span>
                )}
              </div>

              {artist.top_track_name && (
                <div className="bmg-top-track">
                  <strong>Top Track</strong>
                  <div>{artist.top_track_name}</div>
                  {artist.top_track_streams && (
                    <div className="bmg-stream-count">
                      {formatFollowers(artist.top_track_streams)} streams
                    </div>
                  )}
                </div>
              )}

              {(artist.album_count > 0 || artist.single_count > 0) && (
                <div className="bmg-catalog-stats">
                  {artist.album_count > 0 && <span>{artist.album_count} albums</span>}
                  {artist.album_count > 0 && artist.single_count > 0 && <span> • </span>}
                  {artist.single_count > 0 && <span>{artist.single_count} singles</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render detail view
  if (view === 'detail' && selectedArtist) {
    return (
      <div className="bmg-container bmg-detail-view">
        <button onClick={handleBackClick} className="bmg-back-button">
          <ArrowLeft size={16} /> Back to Artists
        </button>

        <div className="bmg-detail-header">
          <div className="bmg-detail-title-section">
            <h2>{selectedArtist.artist_name}</h2>
            <div className="bmg-last-updated">
              Data from: {new Date().toLocaleDateString()} • Updated daily
            </div>
          </div>
          <button
            onClick={handleRefreshInsights}
            className="bmg-refresh-button"
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'bmg-spinner' : ''} /> {refreshing ? 'Refreshing...' : 'Refresh Insights'}
          </button>
        </div>

        {/* Hero Stats Summary Bar */}
        <div className="bmg-hero-stats">
          {selectedArtist.monthly_listeners && (
            <div className="bmg-hero-stat">
              <div className="bmg-hero-stat-value">{formatFollowers(selectedArtist.monthly_listeners)}</div>
              <div className="bmg-hero-stat-label">Spotify Monthly Listeners</div>
            </div>
          )}
          {selectedArtist.world_rank && (
            <div className="bmg-hero-stat">
              <div className="bmg-hero-stat-value">#{selectedArtist.world_rank.toLocaleString()}</div>
              <div className="bmg-hero-stat-label">Spotify Global Rank</div>
            </div>
          )}
          {billboardData.length > 0 && billboardData[0].billboard_position && (
            <div className="bmg-hero-stat">
              <div className="bmg-hero-stat-value">#{billboardData[0].billboard_position}</div>
              <div className="bmg-hero-stat-label">Billboard Hot 100</div>
            </div>
          )}
          {selectedArtist.top_track_streams && (
            <div className="bmg-hero-stat">
              <div className="bmg-hero-stat-value">{formatFollowers(selectedArtist.top_track_streams)}</div>
              <div className="bmg-hero-stat-label">Top Track Streams</div>
            </div>
          )}
        </div>

        {detailLoading ? (
          <div className="bmg-loading">Loading artist details...</div>
        ) : (
          <>
            {/* Current Metrics Dashboard */}
            <div className="bmg-metrics-dashboard">
              {/* Spotify Card */}
              <div className="bmg-metric-card">
                <div className="bmg-metric-header">
                  <div className="bmg-metric-icon spotify">
                    <Music size={20} color="#1DB954" />
                  </div>
                  <div className="bmg-metric-label-container">
                    <div className="bmg-metric-label">Spotify</div>
                    {artistInsights && artistInsights.momentum_indicator && (
                      <span className={`bmg-card-momentum-badge ${getMomentumClass(artistInsights.momentum_indicator)}`}>
                        {artistInsights.momentum_indicator === 'Rising' && '🔥 '}
                        {artistInsights.momentum_indicator === 'Declining' && '📉 '}
                        {artistInsights.momentum_indicator}
                      </span>
                    )}
                  </div>
                </div>
                <div className="bmg-metric-value">{selectedArtist.spotify_popularity}</div>
                <div className="bmg-metric-secondary">Popularity Score</div>
                <div className="bmg-metric-secondary">
                  <strong>{formatFollowers(selectedArtist.spotify_followers)}</strong> followers
                </div>
                {selectedArtist.top_track_name && (
                  <div className="bmg-metric-tertiary">
                    Top: {selectedArtist.top_track_name}
                  </div>
                )}
              </div>

              {/* Apple Music Card */}
              <div className="bmg-metric-card">
                <div className="bmg-metric-header">
                  <div className="bmg-metric-icon apple">
                    <Music size={20} color="#FA243C" />
                  </div>
                  <div className="bmg-metric-label">Apple Music (Global)</div>
                </div>
                {(() => {
                  // Primary: use appleChartData (7 countries, overall + genre)
                  const hasNewData = appleChartData && Object.keys(appleChartData).length > 0;

                  let regions = [];
                  if (hasNewData) {
                    // For each country, pick best entry: overall if exists, else best genre
                    Object.entries(appleChartData).forEach(([cc, data]) => {
                      const config = COUNTRY_CONFIG.find(c => c.code === cc);
                      if (!config) return;
                      const overall = data.overall;
                      const bestGenre = (data.genres || []).length > 0
                        ? data.genres.reduce((a, b) => a.chart_position < b.chart_position ? a : b)
                        : null;
                      const pick = overall || bestGenre;
                      if (pick) {
                        regions.push({
                          name: config.name,
                          flag: config.flag,
                          position: pick.chart_position,
                          song: pick.song_name,
                          chartLabel: overall ? 'Overall Chart' : `${bestGenre.genre_name} Chart`
                        });
                      }
                    });
                  } else {
                    // Fallback: old 3-country fields from selectedArtist
                    regions = [
                      { name: 'US', flag: '🇺🇸', position: selectedArtist.apple_music_chart_position_us, song: selectedArtist.apple_top_song_name_us, chartLabel: 'Overall Chart' },
                      { name: 'UK', flag: '🇬🇧', position: selectedArtist.apple_music_chart_position_uk, song: selectedArtist.apple_top_song_name_uk, chartLabel: 'Overall Chart' },
                      { name: 'DE', flag: '🇩🇪', position: selectedArtist.apple_music_chart_position_de, song: selectedArtist.apple_top_song_name_de, chartLabel: 'Overall Chart' }
                    ].filter(r => r.position);
                  }

                  if (regions.length === 0) {
                    return (
                      <div className="bmg-not-charting-wrapper">
                        <div className="bmg-not-charting">Not in Top 100</div>
                        <div className="bmg-not-charting-context">Check back for chart updates</div>
                      </div>
                    );
                  }

                  // Find best position (lowest number) across all countries
                  const bestRegion = regions.reduce((prev, curr) => prev.position < curr.position ? prev : curr);

                  // Calculate average position across all charting countries
                  const avgPosition = Math.round(regions.reduce((sum, r) => sum + r.position, 0) / regions.length);

                  return (
                    <>
                      <div className="bmg-metric-value">#{bestRegion.position}</div>
                      <div className="bmg-metric-secondary">{bestRegion.flag} {bestRegion.name} — {bestRegion.chartLabel}</div>
                      <div className="bmg-metric-secondary">
                        <strong>Avg: #{avgPosition}</strong> across {regions.length} region{regions.length > 1 ? 's' : ''}
                      </div>
                      {bestRegion.song && (
                        <div className="bmg-metric-tertiary">
                          {bestRegion.song}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Billboard Card */}
              <div className="bmg-metric-card">
                <div className="bmg-metric-header">
                  <div className="bmg-metric-icon billboard">
                    <BarChart3 size={20} color="#FFD700" />
                  </div>
                  <div className="bmg-metric-label">Billboard Hot 100</div>
                </div>
                {(() => {
                  // Find the most recent entry with a billboard_position
                  const latestBillboard = billboardData.find(entry => entry.billboard_position);
                  return latestBillboard ? (
                    <>
                      <div className="bmg-metric-value">#{latestBillboard.billboard_position}</div>
                      <div className="bmg-metric-secondary">
                        {new Date(latestBillboard.metric_date).toDateString() === new Date().toDateString()
                          ? 'Current Position'
                          : `Last Position (${new Date(latestBillboard.metric_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
                        }
                      </div>
                      {latestBillboard.billboard_song_title && (
                        <div className="bmg-metric-tertiary">
                          {latestBillboard.billboard_song_title}
                        </div>
                      )}
                      {latestBillboard.billboard_weeks_on_chart && (
                        <div className="bmg-metric-tertiary">
                          {latestBillboard.billboard_weeks_on_chart} weeks on chart
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bmg-not-charting-wrapper">
                      <div className="bmg-not-charting">Not in Hot 100</div>
                      <div className="bmg-not-charting-context">Tracking for chart entry</div>
                    </div>
                  );
                })()}
              </div>

              {/* Deezer Card (France) */}
              <div className="bmg-metric-card">
                <div className="bmg-metric-header">
                  <div className="bmg-metric-icon deezer">
                    <Music size={20} color="#FF0092" />
                  </div>
                  <div className="bmg-metric-label">Deezer 🇫🇷 France</div>
                </div>
                {(selectedArtist.deezer_artist_chart_position_fr || selectedArtist.deezer_track_chart_position_fr || selectedArtist.deezer_followers) ? (
                  <>
                    {selectedArtist.deezer_artist_chart_position_fr ? (
                      <>
                        <div className="bmg-metric-value">#{selectedArtist.deezer_artist_chart_position_fr}</div>
                        <div className="bmg-metric-secondary">Artist Chart Position</div>
                      </>
                    ) : selectedArtist.deezer_track_chart_position_fr ? (
                      <>
                        <div className="bmg-metric-value">#{selectedArtist.deezer_track_chart_position_fr}</div>
                        <div className="bmg-metric-secondary">Track Chart Position</div>
                      </>
                    ) : (
                      <div className="bmg-metric-value">—</div>
                    )}
                    {selectedArtist.deezer_followers && (
                      <div className="bmg-metric-secondary">
                        <strong>{formatFollowers(selectedArtist.deezer_followers)}</strong> fans
                      </div>
                    )}
                    {selectedArtist.deezer_charting_track_name_fr && (
                      <div className="bmg-metric-tertiary">
                        {selectedArtist.deezer_charting_track_name_fr}
                        {selectedArtist.deezer_charting_track_rank && ` (Rank #${selectedArtist.deezer_charting_track_rank})`}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bmg-not-charting-wrapper">
                    <div className="bmg-not-charting">Not on Deezer Charts</div>
                    <div className="bmg-not-charting-context">Tracking for chart entry</div>
                  </div>
                )}
              </div>

              {/* Catalog Card */}
              <div className="bmg-metric-card">
                <div className="bmg-metric-header">
                  <div className="bmg-metric-icon catalog">
                    <Music size={20} color="#8b5cf6" />
                  </div>
                  <div className="bmg-metric-label">Catalog</div>
                </div>
                {(selectedArtist.album_count > 0 || selectedArtist.single_count > 0) ? (
                  <>
                    <div className="bmg-metric-value">
                      {(selectedArtist.album_count || 0) + (selectedArtist.single_count || 0)}
                    </div>
                    <div className="bmg-metric-secondary">Total Releases</div>
                    {selectedArtist.album_count > 0 && (
                      <div className="bmg-metric-secondary">
                        <strong>{selectedArtist.album_count}</strong> albums
                      </div>
                    )}
                    {selectedArtist.single_count > 0 && (
                      <div className="bmg-metric-secondary">
                        <strong>{selectedArtist.single_count}</strong> singles
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bmg-not-charting">No catalog data</div>
                )}
              </div>
            </div>

            {/* Apple Music Regional Comparison — dynamic 7 countries with genre pills */}
            {(() => {
              // Use new API data if available, fall back to old hardcoded fields
              const hasNewData = appleChartData && Object.keys(appleChartData).length > 0;
              const hasOldData = selectedArtist.apple_music_chart_position_us || selectedArtist.apple_music_chart_position_uk || selectedArtist.apple_music_chart_position_de;

              if (!hasNewData && !hasOldData) return null;

              return (
                <div className="bmg-charts-section">
                  <h3 className="bmg-section-title">Apple Music Regional Performance</h3>
                  <div className="bmg-regional-comparison">
                    {hasNewData ? (
                      COUNTRY_CONFIG.map(country => {
                        const data = appleChartData[country.code];
                        const overall = data?.overall;
                        const genres = data?.genres || [];
                        // Primary display: overall position, or best genre position
                        const bestGenre = genres.length > 0
                          ? genres.reduce((a, b) => (a.chart_position < b.chart_position ? a : b))
                          : null;
                        const primary = overall || bestGenre;

                        return (
                          <div className="bmg-region-card" key={country.code}>
                            <div className="bmg-region-header">
                              <span className="bmg-region-flag">{country.flag}</span>
                              <h4>{country.name}</h4>
                            </div>
                            {primary ? (
                              <>
                                <div className="bmg-region-position">#{primary.chart_position}</div>
                                <div className="bmg-region-label">
                                  {overall ? 'Overall Chart' : `${bestGenre.genre_name} Chart`}
                                </div>
                                {primary.song_name && (
                                  <div className="bmg-region-song">{primary.song_name}</div>
                                )}
                                {(() => {
                                  const releaseDate = formatReleaseDateShort(primary.song_release_date);
                                  if (releaseDate) {
                                    return <div className="bmg-region-date">Released {releaseDate}</div>;
                                  }
                                  return null;
                                })()}
                                {primary.chart_trend && (
                                  <div className="bmg-region-trend">
                                    {primary.chart_trend === 'stable' ? 'Stable' : primary.chart_trend === 'rising' ? 'Rising' : primary.chart_trend}
                                  </div>
                                )}
                                {primary.songs_in_chart > 0 && (
                                  <div className="bmg-region-count">{primary.songs_in_chart} song(s) in chart</div>
                                )}
                                {genres.length > 0 && (
                                  <div className="bmg-genre-pills">
                                    {genres
                                      .sort((a, b) => a.chart_position - b.chart_position)
                                      .map((g, i) => (
                                        <span className="bmg-genre-pill" key={i}>
                                          {g.genre_name} #{g.chart_position}
                                        </span>
                                      ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="bmg-region-not-charting">Not in Top 100</div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      /* Fallback: old 3-country hardcoded rendering */
                      [
                        { flag: '\u{1F1FA}\u{1F1F8}', name: 'United States', suffix: 'us' },
                        { flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom', suffix: 'uk' },
                        { flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany', suffix: 'de' },
                      ].map(r => {
                        const pos = selectedArtist[`apple_music_chart_position_${r.suffix}`];
                        return (
                          <div className="bmg-region-card" key={r.suffix}>
                            <div className="bmg-region-header">
                              <span className="bmg-region-flag">{r.flag}</span>
                              <h4>{r.name}</h4>
                            </div>
                            {pos ? (
                              <>
                                <div className="bmg-region-position">#{pos}</div>
                                <div className="bmg-region-label">Chart Position</div>
                                {selectedArtist[`apple_top_song_name_${r.suffix}`] && (
                                  <div className="bmg-region-song">{selectedArtist[`apple_top_song_name_${r.suffix}`]}</div>
                                )}
                                {(() => {
                                  const releaseDate = formatReleaseDateShort(selectedArtist[`apple_top_song_release_date_${r.suffix}`]);
                                  if (releaseDate) {
                                    return <div className="bmg-region-date">Released {releaseDate}</div>;
                                  }
                                  return null;
                                })()}
                                {selectedArtist[`apple_music_chart_trend_${r.suffix}`] && (
                                  <div className="bmg-region-trend">
                                    {selectedArtist[`apple_music_chart_trend_${r.suffix}`] === 'stable' ? 'Stable' : 'Not Charting'}
                                  </div>
                                )}
                                {selectedArtist[`apple_songs_in_chart_${r.suffix}`] > 0 && (
                                  <div className="bmg-region-count">{selectedArtist[`apple_songs_in_chart_${r.suffix}`]} song(s) in chart</div>
                                )}
                              </>
                            ) : (
                              <div className="bmg-region-not-charting">Not in Top 100</div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Top Tracks Section */}
            {selectedArtist.top_tracks && selectedArtist.top_tracks.length > 0 && (
              <div className="bmg-charts-section">
                <h3 className="bmg-section-title">Top Tracks <span style={{ fontSize: '14px', color: '#999', fontWeight: '400' }}>• Sorted by stream velocity</span></h3>
                <div className="bmg-top-tracks-list">
                  {[...selectedArtist.top_tracks]
                    .sort((a, b) => b.streams - a.streams)
                    .map((track, index) => {
                      // Calculate velocity (streams per day) if release_date available
                      let velocity = null;
                      let daysOld = null;
                      let ageBadge = null;

                      if (track.release_date) {
                        const releaseDate = new Date(track.release_date);
                        const today = new Date();
                        daysOld = Math.floor((today - releaseDate) / (1000 * 60 * 60 * 24));

                        if (daysOld > 0) {
                          velocity = Math.floor(track.streams / daysOld);

                          // Determine age badge
                          if (daysOld <= 30) {
                            ageBadge = { text: 'NEW', emoji: '🆕', class: 'bmg-badge-new' };
                          } else if (velocity > 500000) {
                            ageBadge = { text: 'HOT', emoji: '🔥', class: 'bmg-badge-hot' };
                          } else if (daysOld >= 90 && daysOld <= 180 && velocity > 300000) {
                            ageBadge = { text: 'RISING', emoji: '📈', class: 'bmg-badge-rising' };
                          } else if (daysOld > 365) {
                            ageBadge = { text: 'CLASSIC', emoji: '💎', class: 'bmg-badge-classic' };
                          }
                        }
                      }

                      // Calculate max velocity for progress bar
                      const tracksWithVelocity = selectedArtist.top_tracks
                        .map(t => {
                          if (t.release_date) {
                            const rd = new Date(t.release_date);
                            const days = Math.floor((new Date() - rd) / (1000 * 60 * 60 * 24));
                            return days > 0 ? t.streams / days : 0;
                          }
                          return 0;
                        });
                      const maxVelocity = Math.max(...tracksWithVelocity, 1);
                      const barWidth = velocity ? (velocity / maxVelocity) * 100 : (track.streams / Math.max(...selectedArtist.top_tracks.map(t => t.streams))) * 100;

                      return (
                        <div key={index} className="bmg-top-track-item">
                          <div className="bmg-track-rank">#{index + 1}</div>
                          <div className="bmg-track-details">
                            <div className="bmg-track-header">
                              <span className="bmg-track-name">{track.name}</span>
                              {ageBadge && (
                                <span className={`bmg-track-badge ${ageBadge.class}`}>
                                  {ageBadge.emoji} {ageBadge.text}
                                </span>
                              )}
                            </div>
                            <div className="bmg-track-meta">
                              <span className="bmg-track-total">{formatFollowers(track.streams)} total streams</span>
                              {velocity && (
                                <>
                                  <span className="bmg-meta-separator">•</span>
                                  <span className="bmg-track-velocity">{formatFollowers(velocity)}/day</span>
                                </>
                              )}
                              {track.release_date && (
                                <>
                                  <span className="bmg-meta-separator">•</span>
                                  <span className="bmg-track-date">
                                    Released {new Date(track.release_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="bmg-track-bar-container">
                            <div
                              className="bmg-track-bar"
                              style={{ width: `${barWidth}%` }}
                            />
                            {velocity && (
                              <div className="bmg-track-bar-label">
                                {formatFollowers(velocity)}/day
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Top Cities Section */}
            {selectedArtist.top_cities && selectedArtist.top_cities.length > 0 && (
              <div className="bmg-charts-section">
                <h3 className="bmg-section-title">Top Cities</h3>
                <div className="bmg-top-cities-container">
                  {selectedArtist.top_cities.map((cityData, index) => {
                    const maxListeners = Math.max(...selectedArtist.top_cities.map(c => c.numberOfListeners));
                    const percentage = (cityData.numberOfListeners / maxListeners) * 100;
                    return (
                      <div key={index} className="bmg-city-row">
                        <div className="bmg-city-info">
                          <span className="bmg-city-flag">{cityData.country}</span>
                          <span className="bmg-city-name">{cityData.city}</span>
                        </div>
                        <div className="bmg-city-bar-container">
                          <div
                            className="bmg-city-bar"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="bmg-city-listeners">
                          {formatFollowers(cityData.numberOfListeners)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Trends & Charts */}
            {timeSeriesData.length > 0 && (
              <div className="bmg-charts-section">
                <h3 className="bmg-section-title">Trends & Performance <span style={{ fontSize: '14px', color: '#999', fontWeight: '400' }}>• Click any chart to zoom</span></h3>
                <div className="bmg-charts-grid">
                  {/* Spotify Popularity Chart */}
                  <div
                    className="bmg-chart-card bmg-chart-clickable"
                    onClick={() => setChartModal({ isOpen: true, chartType: 'popularity', chartData: timeSeriesData })}
                  >
                    <div className="bmg-chart-title">Spotify Popularity (14 Days)</div>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                          domain={getPopularityDomain(timeSeriesData)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px'
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="spotify_popularity"
                          stroke="#1DB954"
                          strokeWidth={2}
                          dot={{ fill: '#1DB954', r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Follower Growth Chart - Daily Change */}
                  <div
                    className="bmg-chart-card bmg-chart-clickable"
                    onClick={() => setChartModal({ isOpen: true, chartType: 'followers_delta', chartData: timeSeriesData })}
                  >
                    <div className="bmg-chart-title">
                      Daily Follower Change (14 Days)
                      {timeSeriesData.length > 0 && (
                        <span style={{ fontSize: '13px', color: '#666', fontWeight: '400', marginLeft: '8px' }}>
                          • Total: {formatFollowers(timeSeriesData[timeSeriesData.length - 1]?.spotify_followers)}
                        </span>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={(() => {
                        // Sort data by date first (oldest to newest)
                        const sortedData = [...timeSeriesData].sort((a, b) =>
                          new Date(a.metric_date) - new Date(b.metric_date)
                        );

                        // Calculate daily deltas, skipping the first entry entirely
                        const dataWithDeltas = [];
                        for (let i = 1; i < sortedData.length; i++) {
                          const prevFollowers = sortedData[i - 1].spotify_followers;
                          const currentFollowers = sortedData[i].spotify_followers;
                          const delta = currentFollowers - prevFollowers;

                          // Skip if delta is unreasonably large (> 1M = likely bad data or initial count)
                          if (Math.abs(delta) > 1000000) {
                            continue;
                          }

                          dataWithDeltas.push({
                            ...sortedData[i],
                            follower_change: delta
                          });
                        }
                        return dataWithDeltas;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                          tickFormatter={(value) => value >= 0 ? `+${formatFollowers(value)}` : formatFollowers(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [
                            value >= 0 ? `+${formatFollowersExact(value)}` : formatFollowersExact(value),
                            'Daily Change'
                          ]}
                        />
                        <Bar
                          dataKey="follower_change"
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Monthly Listeners Chart - Daily Change */}
                  <div
                    className="bmg-chart-card bmg-chart-clickable"
                    onClick={() => setChartModal({ isOpen: true, chartType: 'monthly_listeners_delta', chartData: timeSeriesData })}
                  >
                    <div className="bmg-chart-title">
                      Daily Monthly Listener Change (14 Days)
                      {timeSeriesData.length > 0 && (
                        <span style={{ fontSize: '13px', color: '#666', fontWeight: '400', marginLeft: '8px' }}>
                          • Total: {formatFollowers(timeSeriesData[timeSeriesData.length - 1]?.monthly_listeners)}
                        </span>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={(() => {
                        // Sort data by date first (oldest to newest)
                        const sortedData = [...timeSeriesData].sort((a, b) =>
                          new Date(a.metric_date) - new Date(b.metric_date)
                        );

                        // Calculate daily deltas, skipping the first entry entirely
                        const dataWithDeltas = [];
                        for (let i = 1; i < sortedData.length; i++) {
                          const prevListeners = sortedData[i - 1].monthly_listeners;
                          const currentListeners = sortedData[i].monthly_listeners;
                          const delta = currentListeners - prevListeners;

                          // Skip if delta is unreasonably large (> 5M = likely bad data or initial count)
                          if (Math.abs(delta) > 5000000) {
                            continue;
                          }

                          dataWithDeltas.push({
                            ...sortedData[i],
                            listener_change: delta
                          });
                        }
                        return dataWithDeltas;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                          tickFormatter={(value) => value >= 0 ? `+${formatFollowers(value)}` : formatFollowers(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [
                            value >= 0 ? `+${formatFollowersExact(value)}` : formatFollowersExact(value),
                            'Daily Change'
                          ]}
                        />
                        <Bar
                          dataKey="listener_change"
                          fill="#8b5cf6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* World Rank Chart */}
                  <div
                    className="bmg-chart-card bmg-chart-clickable"
                    onClick={() => setChartModal({ isOpen: true, chartType: 'world_rank', chartData: timeSeriesData })}
                  >
                    <div className="bmg-chart-title">Spotify World Rank (14 Days)</div>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                          reversed
                          tickFormatter={(value) => `#${value.toLocaleString()}`}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`#${value.toLocaleString()}`, 'World Rank']}
                        />
                        <Line
                          type="monotone"
                          dataKey="world_rank"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          dot={{ fill: '#f59e0b', r: 4 }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Deezer Followers Chart - Daily Change */}
                  {timeSeriesData.some(d => d.deezer_followers) && (
                    <div
                      className="bmg-chart-card bmg-chart-clickable"
                      onClick={() => setChartModal({ isOpen: true, chartType: 'deezer_followers_delta', chartData: timeSeriesData })}
                    >
                      <div className="bmg-chart-title">
                        Daily Deezer Fan Change (14 Days)
                        {timeSeriesData.length > 0 && timeSeriesData[timeSeriesData.length - 1]?.deezer_followers && (
                          <span style={{ fontSize: '13px', color: '#666', fontWeight: '400', marginLeft: '8px' }}>
                            • Total: {formatFollowers(timeSeriesData[timeSeriesData.length - 1]?.deezer_followers)}
                          </span>
                        )}
                      </div>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={(() => {
                          // Sort data by date first (oldest to newest)
                          const sortedData = [...timeSeriesData].sort((a, b) =>
                            new Date(a.metric_date) - new Date(b.metric_date)
                          );

                          // Calculate daily deltas, skipping the first entry entirely
                          const dataWithDeltas = [];
                          for (let i = 1; i < sortedData.length; i++) {
                            const prevFans = sortedData[i - 1].deezer_followers;
                            const currentFans = sortedData[i].deezer_followers;

                            if (!prevFans || !currentFans) continue;

                            const delta = currentFans - prevFans;

                            // Skip if delta is unreasonably large (> 1M = likely bad data or initial count)
                            if (Math.abs(delta) > 1000000) {
                              continue;
                            }

                            dataWithDeltas.push({
                              ...sortedData[i],
                              fan_change: delta
                            });
                          }
                          return dataWithDeltas;
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                          <XAxis
                            dataKey="metric_date"
                            tickFormatter={formatDate}
                            stroke="#666"
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis
                            stroke="#666"
                            style={{ fontSize: '12px' }}
                            tickFormatter={(value) => value >= 0 ? `+${formatFollowers(value)}` : formatFollowers(value)}
                          />
                          <Tooltip
                            contentStyle={{
                              background: 'white',
                              border: '1px solid #e5e5e5',
                              borderRadius: '8px'
                            }}
                            formatter={(value) => [
                              value >= 0 ? `+${formatFollowersExact(value)}` : formatFollowersExact(value),
                              'Daily Change'
                            ]}
                          />
                          <Bar
                            dataKey="fan_change"
                            fill="#FF0092"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billboard Trajectory Chart */}
            {billboardData.length > 0 && (
              <div className="bmg-charts-section">
                <h3 className="bmg-section-title">Billboard Hot 100 History</h3>
                <div className="bmg-chart-card">
                  <div className="bmg-chart-title">
                    {billboardData[0]?.billboard_song_title} - Chart Position Over Time
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={billboardData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis
                        dataKey="metric_date"
                        tickFormatter={formatDate}
                        stroke="#666"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis
                        stroke="#666"
                        style={{ fontSize: '12px' }}
                        reversed
                        domain={[1, 100]}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'white',
                          border: '1px solid #e5e5e5',
                          borderRadius: '8px'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="billboard_position"
                        stroke="#FFD700"
                        strokeWidth={2}
                        dot={{ fill: '#FFD700', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {billboardData[0]?.billboard_weeks_on_chart && (
                    <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                      <strong>Peak Position:</strong> #{billboardData[0].billboard_peak_position} •
                      <strong> Weeks on Chart:</strong> {billboardData[0].billboard_weeks_on_chart}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Insights */}
            {artistInsights ? (
              <div className="bmg-insights-section">
                <h3 className="bmg-section-title">AI-Generated Insights</h3>

                <div className="bmg-insights-header">
                  {artistInsights.momentum_indicator && (
                    <span className={`bmg-momentum-badge ${getMomentumClass(artistInsights.momentum_indicator)}`}>
                      {artistInsights.momentum_indicator}
                    </span>
                  )}
                  {artistInsights.platform_profile && (
                    <span className="bmg-platform-profile-badge">
                      {artistInsights.platform_profile}
                    </span>
                  )}
                  {artistInsights.growth_stage && (
                    <span className="bmg-growth-stage-badge">
                      {artistInsights.growth_stage}
                    </span>
                  )}
                </div>

                {artistInsights.executive_summary && (
                  <div className="bmg-executive-summary">
                    <h3>Executive Summary</h3>
                    <p>{artistInsights.executive_summary}</p>
                  </div>
                )}

                {artistInsights.detailed_analysis && (
                  <div className="bmg-detailed-analysis">
                    <h3>Detailed Analysis</h3>
                    <p>{artistInsights.detailed_analysis}</p>
                  </div>
                )}

                {artistInsights.key_opportunities && (
                  <div className="bmg-key-opportunities">
                    <h3>Key Opportunities</h3>
                    <div>{formatKeyOpportunities(artistInsights.key_opportunities)}</div>
                  </div>
                )}

                <div className="bmg-insights-timestamp">
                  Generated {getTimeAgo(artistInsights.generated_at)} •
                  Based on {artistInsights.days_of_data} days of data
                </div>
              </div>
            ) : (
              <div className="bmg-insights-section bmg-no-insights">
                <p>No AI insights available yet for this artist.</p>
                <button onClick={handleRefreshInsights} className="bmg-refresh-button">
                  <RefreshCw size={16} /> Generate Insights
                </button>
              </div>
            )}
          </>
        )}

        {/* Chart Modal */}
        {chartModal.isOpen && (
          <div className="bmg-modal-overlay" onClick={() => setChartModal({ isOpen: false, chartType: null, chartData: null })}>
            <div className="bmg-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="bmg-modal-close" onClick={() => setChartModal({ isOpen: false, chartType: null, chartData: null })}>
                ✕
              </button>
              <div className="bmg-modal-chart">
                {chartModal.chartType === 'popularity' && (
                  <>
                    <h3 className="bmg-modal-title">Spotify Popularity Trend</h3>
                    <ResponsiveContainer width="100%" height={500}>
                      <LineChart data={chartModal.chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                          domain={getPopularityDomain(chartModal.chartData)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="spotify_popularity"
                          stroke="#1DB954"
                          strokeWidth={3}
                          dot={{ fill: '#1DB954', r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
                {chartModal.chartType === 'followers_delta' && (
                  <>
                    <h3 className="bmg-modal-title">
                      Daily Follower Change
                      {chartModal.chartData.length > 0 && (
                        <span style={{ fontSize: '16px', color: '#666', fontWeight: '400', marginLeft: '12px' }}>
                          Total: {formatFollowers(chartModal.chartData[chartModal.chartData.length - 1]?.spotify_followers)}
                        </span>
                      )}
                    </h3>
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart data={(() => {
                        // Sort data by date first (oldest to newest)
                        const sortedData = [...chartModal.chartData].sort((a, b) =>
                          new Date(a.metric_date) - new Date(b.metric_date)
                        );

                        // Calculate daily deltas, skipping the first entry entirely
                        const dataWithDeltas = [];
                        for (let i = 1; i < sortedData.length; i++) {
                          const prevFollowers = sortedData[i - 1].spotify_followers;
                          const currentFollowers = sortedData[i].spotify_followers;
                          const delta = currentFollowers - prevFollowers;

                          // Skip if delta is unreasonably large (> 1M = likely bad data or initial count)
                          if (Math.abs(delta) > 1000000) {
                            continue;
                          }

                          dataWithDeltas.push({
                            ...sortedData[i],
                            follower_change: delta
                          });
                        }
                        return dataWithDeltas;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => value >= 0 ? `+${formatFollowers(value)}` : formatFollowers(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                          formatter={(value) => [
                            value >= 0 ? `+${formatFollowersExact(value)}` : formatFollowersExact(value),
                            'Daily Change'
                          ]}
                        />
                        <Bar
                          dataKey="follower_change"
                          fill="#3b82f6"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
                {chartModal.chartType === 'monthly_listeners_delta' && (
                  <>
                    <h3 className="bmg-modal-title">Daily Monthly Listener Change</h3>
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart data={(() => {
                        // Sort data by date first (oldest to newest)
                        const sortedData = [...chartModal.chartData].sort((a, b) =>
                          new Date(a.metric_date) - new Date(b.metric_date)
                        );

                        // Calculate daily deltas, skipping the first entry entirely
                        const dataWithDeltas = [];
                        for (let i = 1; i < sortedData.length; i++) {
                          const prevListeners = sortedData[i - 1].monthly_listeners;
                          const currentListeners = sortedData[i].monthly_listeners;
                          const delta = currentListeners - prevListeners;

                          // Skip if delta is unreasonably large (> 5M = likely bad data or initial count)
                          if (Math.abs(delta) > 5000000) {
                            continue;
                          }

                          dataWithDeltas.push({
                            ...sortedData[i],
                            listener_change: delta
                          });
                        }
                        return dataWithDeltas;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => value >= 0 ? `+${formatFollowers(value)}` : formatFollowers(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                          formatter={(value) => [
                            value >= 0 ? `+${formatFollowersExact(value)}` : formatFollowersExact(value),
                            'Daily Change'
                          ]}
                        />
                        <Bar
                          dataKey="listener_change"
                          fill="#8b5cf6"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
                {chartModal.chartType === 'world_rank' && (
                  <>
                    <h3 className="bmg-modal-title">Spotify World Rank Trend</h3>
                    <ResponsiveContainer width="100%" height={500}>
                      <LineChart data={chartModal.chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                          reversed
                          tickFormatter={(value) => `#${value.toLocaleString()}`}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                          formatter={(value) => [`#${value.toLocaleString()}`, 'World Rank']}
                        />
                        <Line
                          type="monotone"
                          dataKey="world_rank"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          dot={{ fill: '#f59e0b', r: 6 }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
                {chartModal.chartType === 'deezer_followers_delta' && (
                  <>
                    <h3 className="bmg-modal-title">Daily Deezer Fan Change</h3>
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart data={(() => {
                        // Sort data by date first (oldest to newest)
                        const sortedData = [...chartModal.chartData].sort((a, b) =>
                          new Date(a.metric_date) - new Date(b.metric_date)
                        );

                        // Calculate daily deltas, skipping the first entry entirely
                        const dataWithDeltas = [];
                        for (let i = 1; i < sortedData.length; i++) {
                          const prevFans = sortedData[i - 1].deezer_followers;
                          const currentFans = sortedData[i].deezer_followers;

                          if (!prevFans || !currentFans) continue;

                          const delta = currentFans - prevFans;

                          // Skip if delta is unreasonably large (> 1M = likely bad data or initial count)
                          if (Math.abs(delta) > 1000000) {
                            continue;
                          }

                          dataWithDeltas.push({
                            ...sortedData[i],
                            fan_change: delta
                          });
                        }
                        return dataWithDeltas;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis
                          dataKey="metric_date"
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis
                          stroke="#666"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => value >= 0 ? `+${formatFollowers(value)}` : formatFollowers(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                          formatter={(value) => [
                            value >= 0 ? `+${formatFollowersExact(value)}` : formatFollowersExact(value),
                            'Daily Change'
                          ]}
                        />
                        <Bar
                          dataKey="fan_change"
                          fill="#FF0092"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default BMGStreaming;
